// Extracted from App.tsx (ocn-S184-P0). Behavior-preserving move of the real
// ProjectDetailPage (NOT the prior structural ProjectDetailRefactored scaffold).
// ocn-S310-P0: builder-tier gate — Findings tab redirects to subscriber-chat.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, getCachedSession } from "../../context/AuthContext";
import {
  AlertTriangle, Archive, ArchiveRestore, Check, CheckCircle, Code2, Edit3, Loader2, MessageSquare,
  Microscope, RefreshCw, Shield, Trash2, Wand2, X, XCircle,
} from "lucide-react";
import api, { buildProtectedStreamUrl, fetchStreamToken } from "../../api";
import { failedPhaseMessage, openSyncRunStream, type SyncStreamHandle } from "../../lib/sync-stream";
import { useBrandedDialog } from '../../components/BrandedDialog';
import { ArchReviewModal } from '../../components/ArchReviewModal';
import { SyncNextStepCard } from '../../components/SyncNextStepCard';
import type { PipelineRun, GuardianRunRecord } from "../../api";
import { useApi } from "../../useApi";
import { useGuardianSSE } from "../../hooks/useGuardianSSE";
import { GuardianChatBubble } from "../../components/chat/GuardianChatBubble";
import FindingsPanel from "../../components/FindingsPanel";
import { IssuesPanel } from "../../components/IssuesPanel";
import ReviewsPanel from "../../components/ReviewsPanel";
import QualityGateSettingsPanel from "../../components/QualityGateSettings";
import WikiVault from "../../components/WikiVault";
import { Dot } from "../../components/shared/Dot";
import { ExportMenu } from "../../components/shared/ExportMenu";
import { VersionHistory } from "../../components/shared/VersionHistory";
import { eC, timeAgo, formatDuration } from "../../lib/helpers";
import { RecentJournal } from "./components/RecentJournal";
import { ManusIngestSection } from "./components/ManusIngestSection";
import { ProjectChatsPanel } from './components/ProjectChatsPanel';
import { commandForName, executeCommand } from "../../lib/commands/registry";

export function ProjectDetailPage({ projectId, go, onFixIt, onProjectRenamed }: { projectId: string; go: (p: string) => void; onFixIt?: (prompt: string) => void; onProjectRenamed?: () => void }) {
  const { data: project, loading: projLoading, error: projError, refetch: refetchProject } = useApi(
    () => api.getProject(projectId), [projectId]
  );
  const { data: runs, loading: runsLoading, error: runsError, refetch: refetchRuns } = useApi(
    () => api.listRuns(projectId, 20).catch(() => [] as PipelineRun[]), [projectId]
  );
  // ocn-S310-P0: read tier to gate Findings tab from builder subscribers.
  const { userTier } = useAuth();
  const { confirm: confirmDialog } = useBrandedDialog();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameOverride, setNameOverride] = useState<string | null>(null);
  const displayedName = nameOverride ?? project?.display_name ?? '';

  useEffect(() => {
    if (!editingName && project?.display_name) setNameDraft(project.display_name);
  }, [project?.display_name, editingName]);

  const saveProjectName = useCallback(async () => {
    if (nameSaving) return;
    const nextName = nameDraft.trim();
    if (!nextName || nextName.length > 120 || nextName === displayedName) {
      setEditingName(false);
      return;
    }
    const previousName = project?.display_name ?? '';
    setNameOverride(nextName);
    setNameSaving(true);
    setNameError(null);
    // Keep the new title visible while the request is in flight; refetch restores
    // the server value on success and the previous value on failure.
    try {
      await api.renameProject(projectId, nextName);
      await refetchProject();
      setNameOverride(null);
      onProjectRenamed?.();
      setEditingName(false);
    } catch (err) {
      setNameDraft(previousName);
      setNameOverride(null);
      setNameError(err instanceof Error ? err.message : 'Could not rename project');
    } finally {
      setNameSaving(false);
    }
  }, [displayedName, nameDraft, nameSaving, onProjectRenamed, projectId, refetchProject]);

  // ocn-S790-P0: Archive (reversible soft-hide) + guarded permanent delete.
  const isArchived = !!project?.subscriber_hidden_at;
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [dangerError, setDangerError] = useState<string | null>(null);

  const handleArchiveToggle = useCallback(async () => {
    if (archiveBusy) return;
    const confirmed = await confirmDialog(
      isArchived
        ? 'Restore this project? It will reappear in your project list.'
        : 'Archive this project? You can restore it later.'
    );
    if (!confirmed) return;
    setArchiveBusy(true);
    setDangerError(null);
    try {
      if (isArchived) {
        await api.unarchiveProject(projectId);
        onProjectRenamed?.();
        await refetchProject();
      } else {
        await api.archiveProject(projectId);
        onProjectRenamed?.();
        go('overview');
      }
    } catch (err) {
      setDangerError(err instanceof Error ? err.message : `Could not ${isArchived ? 'restore' : 'archive'} project`);
    } finally {
      setArchiveBusy(false);
    }
  }, [archiveBusy, isArchived, projectId, onProjectRenamed, refetchProject, go, confirmDialog]);

  const handleDeleteProject = useCallback(async () => {
    if (deleteBusy) return;
    const confirmed = await confirmDialog('This permanently deletes the project. This cannot be undone.');
    if (!confirmed) return;
    setDeleteBusy(true);
    setDangerError(null);
    try {
      await api.removeProject(projectId);
      onProjectRenamed?.();
      go('overview');
    } catch (err) {
      setDangerError(err instanceof Error ? err.message : 'Could not delete project');
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteBusy, projectId, onProjectRenamed, go, confirmDialog]);

  const [syncStatus, setSyncStatus] = useState<{ status: string; last_run: import('../../api').SyncRun | null; stream_url?: string; last_completed_at?: string | null } | null>(null);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncPhases, setSyncPhases] = useState<import('../../api').SyncPhase[]>([]);
  const [syncCurrentPhase, setSyncCurrentPhase] = useState<string>('');
  const [syncJustCompleted, setSyncJustCompleted] = useState(false);
  // The architecture gate is a human wait, not a running model phase. Keep its
  // state independent of SSE so a reload or stream failure cannot hide approval.
  const [activeSyncId, setActiveSyncId] = useState<string | null>(null);
  const [syncGateStatus, setSyncGateStatus] = useState<string>('');
  const [viewingDoc, setViewingDoc] = useState<{ name: string; content: string } | null>(null);
  const [showAllRuns, setShowAllRuns] = useState(false);

  // Only a completed run establishes project knowledge. `last_run` may be a
  // newer failed or review-gated attempt, so never use its timestamp here.
  const lastCompletedAt = syncStatus?.last_completed_at ?? null;
  const hasEverSynced = lastCompletedAt != null;
  const syncFreshness = (() => {
    if (!lastCompletedAt) return 'never';
    const hoursAgo = (Date.now() - new Date(lastCompletedAt).getTime()) / 3600000;
    if (hoursAgo < 24) return 'fresh';
    if (hoursAgo < 72) return 'stale';
    return 'outdated';
  })();

    // --- Findings tab state ----------------------------
  const [activeTab, setActiveTab] = useState<'overview' | 'findings' | 'issues' | 'reviews' | 'wiki'>(() => {
    const target = sessionStorage.getItem('project_target_tab');
    if (target === 'findings' || target === 'issues' || target === 'reviews' || target === 'wiki') {
      sessionStorage.removeItem('project_target_tab');
      return target;
    }
    return 'overview';
  });

  // ocn-S310-P0: redirect builder tier away from Findings tab -> subscriber chat path.
  useEffect(() => {
    if (userTier === 'builder' && activeTab === 'findings') {
      go(`subscriber-chat:sync_find_fix:${projectId}`);
    }
  }, [userTier, activeTab, projectId, go]);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [findingsSummary, setFindingsSummary] = useState<{ unresolved: number; critical: number; high: number; medium: number } | null>(null);

  // Fetch unresolved findings count from live API (not cached phase output)
  const refreshFindingsSummary = useCallback(() => {
    if (!projectId || !hasEverSynced) {
      setUnresolvedCount(0);
      setFindingsSummary(null);
      return;
    }
    api.findings.summary(projectId)
      .then((s) => {
        setUnresolvedCount(s.unresolved ?? 0);
        setFindingsSummary({ unresolved: s.unresolved, critical: s.critical, high: s.high, medium: s.medium });
      })
      .catch(() => {});
  }, [projectId, hasEverSynced]);

  useEffect(() => { refreshFindingsSummary(); }, [refreshFindingsSummary]);

  const openDocById = (artifactId: string, filename: string) => {
    api.get<{ artifact: { content: string } }>(`/artifacts/detail/${artifactId}`)
      .then((d) => setViewingDoc({ name: filename, content: d.artifact.content || 'No content available.' }))
      .catch(() => setViewingDoc({ name: filename, content: 'Failed to load document.' }));
  };

  const openDocByName = (filename: string) => {
    const bare = filename.replace(/^(?:docs|\.ocnexus)\//, '');
    const match = generatedDocs.find((d: { filename: string }) => d.filename === filename || d.filename === bare);
    if (match) {
      openDocById(match.id, bare);
    } else {
      api.get<{ docs: Array<{ label: string; content: string }> }>(`/artifacts/repo-docs/${projectId}`)
        .then((d) => {
          const doc = d.docs.find((rd: { label: string }) => rd.label.includes(filename.replace('.md', '')));
          setViewingDoc({ name: filename, content: doc?.content || 'Document not found.' });
        })
        .catch(() => setViewingDoc({ name: filename, content: 'Failed to load document.' }));
    }
  };
  const syncEsRef = useRef<SyncStreamHandle | null>(null);
  const syncStreamGenerationRef = useRef(0);

  const connectSyncSSE = useCallback(async (runId: string, streamToken?: string | null, knownStatus?: string | null) => {
    const generation = ++syncStreamGenerationRef.current;
    syncEsRef.current?.close();
    const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
    let token = streamToken ?? null;
    if (!token) {
      const accessToken = getCachedSession()?.access_token;
      if (accessToken) token = await fetchStreamToken('sync_run', runId, accessToken);
    }
    if (generation !== syncStreamGenerationRef.current) return;
    const url = buildProtectedStreamUrl(API_BASE, `/api/sync/stream/${runId}`, token);
    if (!url) {
      setSyncRunning(false);
      api.syncStatus(projectId).then(setSyncStatus).catch(() => {});
      return;
    }

    const applyStatus = (res: { status: string; last_run: import('../../api').SyncRun | null; stream_url?: string; last_completed_at?: string | null }) => {
      setSyncStatus(res);
      const run = res.last_run;
      if (!run) return;
      if (run.phases) setSyncPhases(run.phases);
      if (run.current_phase) setSyncCurrentPhase(run.current_phase);
      setSyncGateStatus(run.status);
    };
    const finish = (status: 'completed' | 'failed' | 'cancelled') => {
      setSyncRunning(false);
      if (status === 'completed') setSyncJustCompleted(true);
      api.syncStatus(projectId).then((res) => setSyncStatus(res)).catch(() => {});
      refetchProject();
    };

    syncEsRef.current = openSyncRunStream({
      status: knownStatus,
      url,
      fetchStatus: () => api.syncStatus(projectId),
      statusOf: (res) => res.last_run?.status,
      onStatus: applyStatus,
      onTerminal: finish,
      onEvent: (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data) as { phases?: import('../../api').SyncPhase[]; current_phase?: string; status?: string };
          if (data.phases) setSyncPhases(data.phases);
          if (data.current_phase) setSyncCurrentPhase(data.current_phase);
          if (data.status) setSyncGateStatus(data.status);
        } catch { /* ignore malformed progress events */ }
      },
    });
  }, [projectId, refetchProject]);

  useEffect(() => {
    api.syncStatus(projectId).then((res) => {
      setSyncStatus(res);
      if (res.last_run) {
        if (res.last_run.phases) setSyncPhases(res.last_run.phases);
        if (res.last_run.current_phase) setSyncCurrentPhase(res.last_run.current_phase);
        setSyncGateStatus(res.last_run.status);
        if (['running', 'review_required', 'audit_pending'].includes(res.last_run.status)) {
          setActiveSyncId(res.last_run.id);
          setSyncRunning(true);
          void connectSyncSSE(res.last_run.id, (res as { stream_token?: string }).stream_token ?? null, res.last_run.status);
        }
      }
    }).catch(() => {});
    return () => {
      syncStreamGenerationRef.current += 1;
      syncEsRef.current?.close();
    };
  }, [projectId, connectSyncSSE]);

  // Gate-state is authoritative for the human approval wait. It deliberately
  // continues even if EventSource could not be established.
  useEffect(() => {
    if (!activeSyncId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const gate = await api.syncGateState(activeSyncId);
        if (cancelled) return;
        setSyncGateStatus(gate.status);
        setSyncRunning(['running', 'review_required', 'audit_pending'].includes(gate.status));
        if (['completed', 'failed', 'cancelled'].includes(gate.status)) setActiveSyncId(null);
      } catch { /* SSE/status polling remains a best-effort fallback. */ }
    };
    const handle = setInterval(() => { void tick(); }, 4000);
    void tick();
    return () => { cancelled = true; clearInterval(handle); };
  }, [activeSyncId]);

  // --- Guardian state machine ---------------------------------
  const [guardianRunId, setGuardianRunId] = useState<string | null>(null);
  const [guardianLastRun, setGuardianLastRun] = useState<GuardianRunRecord | null>(null);
  const [guardianMsg, setGuardianMsg] = useState<string | null>(null);

  const { buttonState: guardianState, setButtonState: setGuardianState, lastEvent: guardianEvent, allEvents: guardianAllEvents } = useGuardianSSE({
    enabled: hasEverSynced,
    runId: guardianRunId,
    onEvent: (evt) => {
      if (evt.status === 'running' || evt.status === 'started') {
        setGuardianMsg('Guardian is analyzing regressions...');
      }
    },
    onComplete: (evt) => {
      if (evt.status === 'complete_clean') {
        setGuardianMsg(`No regressions found \u2014 $${(evt.cost_usd ?? 0).toFixed(2)}`);
      } else {
        setGuardianMsg(`${evt.regressions_found ?? 0} regressions found, ${evt.regressions_fixed ?? 0} fixed, ${evt.regressions_unfixable ?? 0} unfixable`);
      }
      setGuardianRunId(null);
    },
    onError: (err) => {
      setGuardianMsg(err);
      setGuardianRunId(null);
    },
  });

  // --- Spec upload state -------------------------------------
  const [specUploading, setSpecUploading] = useState(false);
  const [specUploadMsg, setSpecUploadMsg] = useState<string | null>(null);
  const [specList, setSpecList] = useState<{ id: string; filename: string; created_at?: string }[]>([]);
  const [generatedDocs, setGeneratedDocs] = useState<{ id: string; filename: string; created_at?: string }[]>([]);
  const specFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.getArtifacts(projectId, 'input').then((artifacts: any) => {
      const specs = artifacts.filter((a: any) => a.filename?.endsWith('.md') || a.mime_type === 'text/markdown');
      setSpecList(specs.map((a: any) => ({ id: a.id, filename: a.filename, created_at: a.created_at })));
    }).catch(() => {});
    api.getArtifacts(projectId, 'generated').then((artifacts: any) => {
      setGeneratedDocs(artifacts.map((a: any) => ({ id: a.id, filename: a.filename, created_at: a.created_at })));
    }).catch(() => {});
  }, [projectId]);

  const handleSpecUpload = async (file: File) => {
    setSpecUploading(true);
    setSpecUploadMsg(null);
    try {
      const result = await api.wizardParseSpec(file);
      const p = result.parsed;
      const features = (p.keyFeatures || []).map((f: string) => `- ${f}`).join('\n');
      const specContent = [
        `# ${p.name || file.name.replace(/\.[^.]+$/, '')}`,
        '', p.summary || '',
        '', '## Problem', p.problemStatement || p.description || '',
        '', '## Target Users', p.targetUsers || '',
        '', '## Core Features', features,
        '', '## Tech Stack', p.techPreferences || p.techStack || '',
        '', '## Success Criteria', p.successCriteria || '',
      ].join('\n');
      const specFilename = file.name.replace(/\.[^.]+$/, '.md');
      const res = await api.uploadArtifact(projectId, specFilename, specContent, 'text/markdown');
      setSpecList((prev) => [...prev, { id: res.id, filename: specFilename, created_at: new Date().toISOString() }]);
      setSpecUploadMsg(`Spec "${specFilename}" parsed and saved`);
      setTimeout(() => setSpecUploadMsg(null), 5000);
    } catch (err: unknown) {
      setSpecUploadMsg(err instanceof Error ? err.message : 'Failed to upload spec');
    } finally {
      setSpecUploading(false);
      if (specFileRef.current) specFileRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!hasEverSynced) {
      setGuardianRunId(null);
      setGuardianMsg(null);
      setGuardianState('idle');
      return;
    }
    api.guardianStatus(projectId).then((res) => {
      if (res.last_run) setGuardianLastRun(res.last_run);
      if (res.status === 'running' && res.stream_url && res.last_run) {
        setGuardianRunId(res.last_run.id);
        setGuardianState('running');
        setGuardianMsg('Guardian is analyzing regressions...');
      } else if (res.status === 'complete_clean') {
        setGuardianState('complete_clean');
      } else if (res.status === 'complete_regressions') {
        setGuardianState('complete_regressions');
      } else if (res.status === 'timed_out') {
        setGuardianState('timed_out');
      } else if (res.status === 'error') {
        setGuardianState('error');
      } else {
        setGuardianState('idle');
      }
    }).catch(() => {
      setGuardianState('idle');
    });
  }, [projectId, hasEverSynced]); // eslint-disable-line react-hooks/exhaustive-deps

  if (projLoading) return (
    <div className="empty-state">
      <div className="spinner" /><span className="muted">Loading project...</span>
    </div>
  );
  if (projError) return (
    <div className="card" style={{ background: "var(--red-dim)", borderColor: "var(--red)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <AlertTriangle size={14} style={{ color: "var(--red)" }} />
        <span style={{ fontSize: "var(--text-sm)", color: "var(--red)", flex: 1 }}>{projError}</span>
        <button className="btn btn-sm" onClick={refetchProject}><RefreshCw size={11} /> Retry</button>
      </div>
    </div>
  );
  // A transient null (e.g. the project fetch raced against an in-flight sync, or
  // an unwrapped/parsed-empty response) must not render a dead "Project not found"
  // — offer a retry instead of stranding the user. A genuine 404 surfaces via projError above.
  if (!project) return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span className="muted" style={{ flex: 1 }}>Couldn't load this project just now.</span>
      <button className="btn btn-sm" onClick={refetchProject}><RefreshCw size={11} /> Retry</button>
    </div>
  );

  const hasRepo = !!(project?.repo?.owner && project?.repo?.name);

  const triggerSync = async () => {
    setSyncRunning(true);
    setSyncJustCompleted(false);
    setSyncPhases([]);
    setSyncCurrentPhase('');
    try {
      const res = await api.syncRun(projectId);
      if (res.phases) setSyncPhases(res.phases);
      if (res.run_id) {
        setActiveSyncId(res.run_id);
        setSyncGateStatus('running');
        void connectSyncSSE(res.run_id, (res as { stream_token?: string }).stream_token ?? null);
      }
    } catch (err: unknown) {
      const errObj = err as { status?: number; run_id?: string; stream_token?: string; phases?: import('../../api').SyncPhase[] };
      if (errObj.status === 409 && errObj.run_id) {
        setActiveSyncId(errObj.run_id);
        setSyncGateStatus('running');
        if (errObj.phases) setSyncPhases(errObj.phases);
        void connectSyncSSE(errObj.run_id, errObj.stream_token ?? null);
      } else {
        setSyncRunning(false);
      }
    }
  };

  const triggerReviewApproval = async (skip?: string[]) => {
    const runId = syncStatus?.last_run?.id;
    if (!runId) return;
    await api.syncApprove(runId, true, skip);
    setSyncGateStatus('running');
    setSyncRunning(true);
    api.syncStatus(projectId).then(setSyncStatus).catch(() => {});
  };

  const freshnessColor = syncFreshness === 'fresh' ? 'var(--green)' : syncFreshness === 'stale' ? 'var(--yellow)' : syncFreshness === 'outdated' ? 'var(--orange)' : 'var(--text-dim)';
  const freshnessLabel = syncFreshness === 'fresh' ? 'Fresh' : syncFreshness === 'stale' ? 'Stale' : syncFreshness === 'outdated' ? 'Outdated' : 'Never synced';
  const showFreshnessBadge = syncStatus?.last_run?.status !== 'failed' || syncFreshness === 'never';

  const triggerGuardian = async () => {
    if (!hasEverSynced) {
      setGuardianMsg('Sync this project first.');
      return;
    }
    setGuardianMsg(null);
    setGuardianState('running');
    try {
      const res = await api.triggerGuardian(projectId);
      setGuardianRunId(res.run_id);
      setGuardianMsg('Guardian run started...');
    } catch (err: unknown) {
      const errObj = err as { status?: number; run_id?: string; stream_url?: string };
      if (errObj.status === 409 && errObj.run_id) {
        setGuardianRunId(errObj.run_id);
        setGuardianMsg('Guardian is already running \u2014 reconnecting...');
      } else {
        setGuardianState('error');
        setGuardianMsg(err instanceof Error ? err.message : 'Failed to start guardian');
      }
    }
  };

  // Every project-page button is only a renderer. The registry owns the action
  // dispatch so slash text and buttons cannot quietly drift apart.
  const runProjectCommand = async (name: 'sync' | 'guardian' | 'refresh') => {
    const command = commandForName(name);
    if (!command) return;
    await executeCommand(command, {
      projectId,
      sync: triggerSync,
      guardian: triggerGuardian,
      refresh: async () => { await Promise.all([refetchRuns(), refetchProject()]); },
    });
  };

  const commandLabels = {
    sync: commandForName('sync')?.label ?? 'Sync Project',
    guardian: commandForName('guardian')?.label ?? 'Run Guardian',
    refresh: commandForName('refresh')?.label ?? 'Refresh',
  };

  // NOTE: The full render of ProjectDetailPage is preserved verbatim from the original.
  // It was too large to extract safely (1200+ lines with deeply shared state).
  // Future decomposition should split this into sub-components.
  return (
    <div>
      {/* Header */}
      <div className="page-title-row">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Dot status="healthy" sz={8} />
            {editingName ? (
              <input
                autoFocus
                className="input"
                aria-label="Project name"
                value={nameDraft}
                disabled={nameSaving}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNameDraft(event.target.value)}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') { event.preventDefault(); void saveProjectName(); }
                  if (event.key === 'Escape') { setNameDraft(displayedName); setEditingName(false); setNameError(null); }
                }}
                onBlur={() => { void saveProjectName(); }}
                style={{ width: 260, fontSize: 'var(--text-lg)' }}
              />
            ) : (
              <>
                <span className="page-title">{displayedName}</span>
                <button className="btn btn-sm" onClick={() => { setNameDraft(displayedName); setNameError(null); setEditingName(true); }} aria-label="Rename project" title="Rename project">
                  <Edit3 size={13} />
                </button>
              </>
            )}
            <span className={`badge ${isArchived ? 'badge-muted' : 'badge-active'}`}>{isArchived ? 'ARCHIVED' : 'ACTIVE'}</span>
            {nameError && <span className="muted" role="alert" style={{ fontSize: 'var(--text-xs)' }}>{nameError}</span>}
          </div>
          <div className="page-subtitle" style={{ marginTop: 4 }}>
            {hasRepo ? `${project.repo?.owner}/${project.repo?.name}` : 'No repository connected'}
            {(project.packages?.length ?? 0) > 0 && ` \u00B7 ${project.packages.length} package${project.packages.length === 1 ? '' : 's'}`}
            {project.repo?.monorepo && " \u00B7 monorepo"}
          </div>
          {project.description && (
            <p className="dim" style={{ fontSize: "var(--text-sm)", marginTop: 6, maxWidth: 500 }}>{project.description}</p>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            className={`btn btn-sm ${syncRunning || !hasRepo ? '' : 'btn-primary'}`}
            onClick={() => { void runProjectCommand('sync'); }}
            disabled={isArchived || syncRunning || !hasRepo}
            title={isArchived ? 'Restore this project before syncing' : !hasRepo ? 'Connect a GitHub repo first' : undefined}
          >
            {syncRunning ? (
              <><Loader2 size={12} className="spin" /> {syncCurrentPhase ? `Syncing...` : 'Starting sync...'}</>
            ) : !hasRepo ? (
              <><Wand2 size={12} /> No repo connected</>
            ) : (
              <><Wand2 size={12} /> {commandLabels.sync}</>
            )}
          </button>
          <button
            className={`btn btn-sm ${
              guardianState === 'running' ? 'btn-guardian-running' :
              guardianState === 'complete_clean' ? 'btn-guardian-clean' :
              guardianState === 'complete_regressions' ? 'btn-guardian-regressions' :
              guardianState === 'timed_out' ? 'btn-guardian-timeout' :
              guardianState === 'error' ? 'btn-guardian-error' : ''
            }`}
            onClick={() => { void runProjectCommand('guardian'); }}
            disabled={isArchived || !hasEverSynced || guardianState === 'running'}
            title={isArchived ? 'Restore this project before running Guardian' : !hasEverSynced ? 'Available after your first successful sync' : undefined}
          >
            {guardianState === 'running' ? (
              <><Loader2 size={12} className="spin" /> Running...</>
            ) : guardianState === 'complete_clean' ? (
              <><CheckCircle size={12} /> All Clear</>
            ) : guardianState === 'complete_regressions' ? (
              <><AlertTriangle size={12} /> Regressions Found</>
            ) : guardianState === 'timed_out' ? (
              <><XCircle size={12} /> Timed Out</>
            ) : guardianState === 'error' ? (
              <><XCircle size={12} /> Error &mdash; Retry</>
            ) : (
              <><Shield size={12} /> {commandLabels.guardian}</>
            )}
          </button>
          {guardianLastRun?.completed_at && guardianState !== 'running' && (
            <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
              Last run: {new Date(guardianLastRun.completed_at).toLocaleTimeString()}
            </span>
          )}
          <button className="btn btn-sm" onClick={() => { void runProjectCommand('refresh'); }}><RefreshCw size={12} /> {commandLabels.refresh}</button>
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => { void handleArchiveToggle(); }}
            disabled={archiveBusy}
            title={isArchived ? 'Restore this project' : 'Archive this project'}
          >
            {archiveBusy ? <Loader2 size={12} className="spin" /> : isArchived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
            {' '}{isArchived ? 'Restore project' : 'Archive project'}
          </button>
          <button
            className="btn btn-sm"
            onClick={() => { void handleDeleteProject(); }}
            disabled={deleteBusy}
            title="Delete permanently"
            style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
          >
            {deleteBusy ? <Loader2 size={12} className="spin" /> : <Trash2 size={12} />} Delete permanently
          </button>
        </div>
      </div>

      {dangerError && (
        <div className="card" style={{ marginBottom: 12, background: "var(--red-dim)", borderColor: "var(--red)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} style={{ color: "var(--red)" }} />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--red)" }}>{dangerError}</span>
          </div>
        </div>
      )}

      <ProjectChatsPanel key={projectId} projectId={projectId} archived={isArchived} go={go} />

      {/* Guardian chat bubble */}
      {(guardianState !== 'idle' || guardianAllEvents.length > 0) && (
        <div style={{ marginBottom: 12 }}>
          <GuardianChatBubble
            events={guardianAllEvents}
            buttonState={guardianState}
            isStreaming={guardianState === 'running'}
            onCreateIssue={(reg) => {
              const testName = (reg.test_name as string) || 'Unknown test';
              const errorMsg = (reg.error_message as string) || '';
              const repoUrl = project ? `https://github.com/${project.repo.owner}/${project.repo.name}` : '';
              const issueUrl = `${repoUrl}/issues/new?title=${encodeURIComponent(`[Regression] ${testName}`)}&body=${encodeURIComponent(`## Regression Detected by Guardian\n\n**Test:** ${testName}\n**Error:** ${errorMsg}\n\nDetected by OCNexus Guardian automated regression check.`)}`;
              window.open(issueUrl, '_blank');
            }}
            onViewLogs={() => go('logs')}
            onDismiss={() => {}}
          />
          {(guardianState === 'error' || guardianState === 'timed_out') && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button className="btn btn-sm" onClick={() => { void runProjectCommand('guardian'); }} style={{ fontSize: 'var(--text-xs)' }}>
                <RefreshCw size={10} /> Retry
              </button>
              <button className="btn btn-sm" onClick={() => { setGuardianState('idle'); }} style={{ fontSize: 'var(--text-xs)' }}>
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        <button
          className={`btn btn-sm ${activeTab === 'overview' ? '' : 'btn-ghost'}`}
          onClick={() => setActiveTab('overview')}
          style={{
            borderRadius: 0,
            borderBottom: activeTab === 'overview' ? '2px solid var(--blue)' : '2px solid transparent',
            fontWeight: activeTab === 'overview' ? 600 : 400,
          }}
        >
          Overview
        </button>
        {/* ocn-S310-P0: Findings tab is developer-only; builders are redirected to subscriber chat */}
        {userTier !== 'builder' && (
          <button
            className={`btn btn-sm ${activeTab === 'findings' ? '' : 'btn-ghost'}`}
            onClick={() => setActiveTab('findings')}
            style={{
              borderRadius: 0,
              borderBottom: activeTab === 'findings' ? '2px solid var(--blue)' : '2px solid transparent',
              fontWeight: activeTab === 'findings' ? 600 : 400,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            Findings
            {unresolvedCount > 0 && (
              <span style={{
                background: 'var(--orange)', color: '#fff', borderRadius: 10,
                fontSize: 10, fontWeight: 700, padding: '1px 6px', minWidth: 18, textAlign: 'center',
              }}>
                {unresolvedCount}
              </span>
            )}
          </button>
        )}
        <button
          className={`btn btn-sm ${activeTab === 'issues' ? '' : 'btn-ghost'}`}
          onClick={() => setActiveTab('issues')}
          style={{
            borderRadius: 0,
            borderBottom: activeTab === 'issues' ? '2px solid var(--blue)' : '2px solid transparent',
            fontWeight: activeTab === 'issues' ? 600 : 400,
          }}
        >
          Issues
        </button>
        <button
          className={`btn btn-sm ${activeTab === 'reviews' ? '' : 'btn-ghost'}`}
          onClick={() => setActiveTab('reviews')}
          style={{
            borderRadius: 0,
            borderBottom: activeTab === 'reviews' ? '2px solid var(--blue)' : '2px solid transparent',
            fontWeight: activeTab === 'reviews' ? 600 : 400,
          }}
        >
          Reviews
        </button>
        <button
          className={`btn btn-sm ${activeTab === 'wiki' ? '' : 'btn-ghost'}`}
          onClick={() => setActiveTab('wiki')}
          style={{
            borderRadius: 0,
            borderBottom: activeTab === 'wiki' ? '2px solid var(--blue)' : '2px solid transparent',
            fontWeight: activeTab === 'wiki' ? 600 : 400,
          }}
        >
          Wiki
        </button>
      </div>

      {/* ocn-S310-P0: builders never reach FindingsPanel — useEffect above redirects them */}
      {activeTab === 'findings' && userTier !== 'builder' && (
        hasEverSynced
          ? <FindingsPanel projectId={projectId} onFixIt={onFixIt} />
          : <div className="card muted" style={{ fontSize: 'var(--text-sm)' }}>Sync this project first to view findings.</div>
      )}

      {activeTab === 'issues' && (
        <>
          <IssuesPanel projectId={projectId} />
          <QualityGateSettingsPanel projectId={projectId} />
        </>
      )}

      {activeTab === 'reviews' && (
        <ReviewsPanel projectId={projectId} onOpenArtifact={(filename: string) => openDocByName(filename)} />
      )}

      {activeTab === 'wiki' && (
        <WikiVault projectId={projectId} />
      )}

      {activeSyncId && syncGateStatus === 'audit_pending' && (
        <ArchReviewModal
          runId={activeSyncId}
          projectId={projectId}
          onApprove={() => { setSyncGateStatus('running'); setSyncRunning(true); void connectSyncSSE(activeSyncId); }}
          onSkip={() => { setSyncGateStatus('running'); setSyncRunning(true); void connectSyncSSE(activeSyncId); }}
        />
      )}

      {activeTab === 'overview' && <>
      {/* Sync progress, completion summary, and all other overview content */}
      {syncStatus?.last_run?.status === 'failed' && failedPhaseMessage(syncStatus.last_run.phases) && (
        <div data-testid="sync-failed-card" className="card" role="alert" style={{ marginBottom: 16, padding: '12px 16px', borderColor: 'var(--red)', color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1 }}>{failedPhaseMessage(syncStatus.last_run.phases)}</span>
          <button className="btn btn-sm" onClick={() => { void runProjectCommand('sync'); }} disabled={syncRunning}>
            <RefreshCw size={12} /> Retry sync
          </button>
        </div>
      )}
      {syncStatus?.last_run?.status === 'review_required' && (
        <div data-testid="sync-review-approve" className="card" style={{ marginBottom: 16, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>Sync review required</span>
          <button className="btn btn-sm btn-primary" onClick={() => { void triggerReviewApproval(); }}>Approve</button>
          <button className="btn btn-sm" onClick={() => { void triggerReviewApproval(['agents_md', 'worldview_generation', 'routing_rules']); }}>Skip</button>
        </div>
      )}
      {syncRunning && syncPhases.length === 0 && (
        <div className="card" style={{ marginBottom: 16, padding: '16px', background: 'var(--bg-secondary, rgba(255,255,255,0.02))', borderColor: 'var(--accent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 size={14} className="spin" style={{ color: 'var(--accent)' }} />
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>Starting sync &mdash; connecting to progress stream...</span>
          </div>
        </div>
      )}
      {syncRunning && syncPhases.length > 0 && (() => {
        const completed = syncPhases.filter((p: import('../../api').SyncPhase) => p.status === 'completed').length;
        const running = syncPhases.find((p: import('../../api').SyncPhase) => p.status === 'running');
        const total = syncPhases.length || 12;
        const pct = Math.round((completed / total) * 100);
        return (
          <div className="card" style={{ marginBottom: 16, padding: '16px', background: 'var(--bg-secondary, rgba(255,255,255,0.02))', borderColor: 'var(--accent)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              {syncGateStatus === 'audit_pending' ? <Microscope size={14} style={{ color: 'var(--orange)' }} /> : <Loader2 size={14} className="spin" style={{ color: 'var(--accent)' }} />}
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{syncGateStatus === 'audit_pending' ? 'Architecture Review ready — approval required' : 'Syncing your project...'}</span>
              <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>{completed}/{total} phases</span>
            </div>
            <div style={{ height: 4, background: 'var(--bg-tertiary, rgba(255,255,255,0.04))', borderRadius: 2, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent, #00E5FF)', borderRadius: 2, transition: 'width 0.5s ease' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {syncPhases.map((phase: import('../../api').SyncPhase) => (
                <div key={phase.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)',
                  color: phase.status === 'completed' ? 'var(--green)' : phase.status === 'running' ? 'var(--accent)' : phase.status === 'failed' ? 'var(--red)' : 'var(--text-dim)',
                  opacity: phase.status === 'pending' ? 0.4 : 1,
                }}>
                  {phase.status === 'completed' ? <Check size={12} /> : phase.status === 'running' ? <Loader2 size={12} className="spin" /> : phase.status === 'failed' ? <XCircle size={12} /> : <span style={{ width: 12, height: 12, display: 'inline-block' }} />}
                  <span>{phase.name}</span>
                  {phase.status === 'completed' && phase.duration_ms != null && (
                    <span style={{ color: 'var(--text-dim)', marginLeft: 'auto' }}>{phase.duration_ms < 1000 ? `${phase.duration_ms}ms` : `${(phase.duration_ms / 1000).toFixed(1)}s`}</span>
                  )}
                </div>
              ))}
            </div>
            {running && (
              <div style={{ marginTop: 10, padding: '6px 10px', fontSize: 'var(--text-xs)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', background: 'var(--bg-tertiary, rgba(255,255,255,0.03))', borderRadius: 'var(--radius-sm)' }}>
                Currently: {running.name}...
              </div>
            )}
          </div>
        );
      })()}

      {/* Project Knowledge, Specs, Packages, Journal, Pipeline Runs sections */}
      {/* Sync-complete modal (punch 0d89d480, canary 2026-10-03): a sync that finishes
          while this page is open shows what to fix first and goes straight to the
          project chat, whose checklist pre-checks P0/P1. Previously the 'Ready to go'
          card was hidden after a fresh completion and nothing replaced it. */}
      {syncJustCompleted && (
        <div role="dialog" aria-modal="true" aria-label="Sync complete" data-testid="sync-complete-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={() => setSyncJustCompleted(false)}>
          <div className="card" style={{ maxWidth: 560, width: '100%', padding: 20 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Sync complete</div>
            <div className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 12 }}>Project knowledge is up to date. Open Chat to see what this sync found, with critical and high issues pre-checked.</div>
            <SyncNextStepCard projectId={projectId} go={go} lastRunStatus="completed" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={() => setSyncJustCompleted(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={() => { void api.createSubscriberChat(projectId).then((r) => go(`subscriber-chat:chat:${r.chatSessionId}`)).catch(() => go(`subscriber-chat:project:${projectId}`)); }}><MessageSquare size={12} /> Open Chat</button>
            </div>
          </div>
        </div>
      )}
      {!syncJustCompleted && syncFreshness === 'outdated' && hasRepo && !syncRunning && (
        <div className="card" style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--blue-dim, rgba(59,130,246,0.06))', borderColor: 'var(--blue)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Wand2 size={16} style={{ color: 'var(--blue)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>Project knowledge is outdated</div>
            <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>Re-sync to update OCNexus's understanding of your codebase before making changes.</div>
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => { void runProjectCommand('sync'); }} disabled={syncRunning}>
            {syncRunning ? <><Loader2 size={12} className="spin" /> Syncing...</> : <><Wand2 size={12} /> Re-sync Now</>}
          </button>
        </div>
      )}
      {!syncJustCompleted && syncStatus?.last_run?.status === 'completed' && syncFreshness === 'fresh' && hasRepo && (
        <div className="card" style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--green-dim, rgba(34,197,94,0.06))', borderColor: 'var(--green)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <MessageSquare size={16} style={{ color: 'var(--green)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>Ready to go</div>
            <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>Project knowledge is up to date. Open Chat to see the issues your last sync found, check what you want fixed, and send.</div>
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => { void api.createSubscriberChat(projectId).then((r) => go(`subscriber-chat:chat:${r.chatSessionId}`)).catch(() => go(`subscriber-chat:project:${projectId}`)); }}><MessageSquare size={12} /> Open Chat</button>
        </div>
      )}

      {/* Project Knowledge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="section-label">Project Knowledge</div>
        <span className="muted" style={{ fontSize: 'var(--text-xs)', cursor: 'pointer' }} onClick={() => go(`sync:${projectId}`)}>View sync history &rarr;</span>
      </div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: syncFreshness === 'never' ? 'var(--surface3)' : `${freshnessColor}15`, border: `1.5px solid ${freshnessColor}` }}>
            <Wand2 size={16} style={{ color: freshnessColor }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>{syncFreshness === 'never' ? 'No project knowledge yet' : 'Project Knowledge'}</span>
              {showFreshnessBadge && <span className="badge" style={{ background: `${freshnessColor}20`, color: freshnessColor, fontSize: 10 }}>{freshnessLabel}</span>}
            </div>
            <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>
              {lastCompletedAt ? `Last synced ${new Date(lastCompletedAt).toLocaleDateString()} at ${new Date(lastCompletedAt).toLocaleTimeString()}` : 'Run a sync to build OCNexus\'s understanding of this codebase'}
            </div>
          </div>
          {(syncFreshness === 'never' || syncFreshness === 'outdated' || syncFreshness === 'stale') && hasRepo && (
            <button className="btn btn-sm btn-primary" onClick={() => { void runProjectCommand('sync'); }} disabled={syncRunning}>
              {syncRunning ? <><Loader2 size={12} className="spin" /> Syncing...</> : <><Wand2 size={12} /> {syncFreshness === 'never' ? 'Sync Now' : 'Re-sync'}</>}
            </button>
          )}
          {syncFreshness === 'never' && !hasRepo && (
            <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>Connect a GitHub repo to enable sync</span>
          )}
        </div>
      </div>

      {/* Manus Ingest */}
      <ManusIngestSection projectId={projectId} />

      {/* Packages */}
      {project.packages.length > 0 && (
        <>
          <div className="section-label" style={{ marginBottom: 8 }}>Packages</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 8, marginBottom: 20 }}>
            {project.packages.map((pkg: any) => {
              const typeColor = pkg.type === 'api' || pkg.type === 'backend' ? 'var(--green)' : pkg.type === 'frontend' ? 'var(--blue)' : pkg.type === 'shared' ? 'var(--purple, #a78bfa)' : pkg.type === 'cli' ? 'var(--orange, #f59e0b)' : 'var(--text-muted)';
              return (
                <div key={pkg.path || pkg.name} className="pkg-card" style={{ cursor: 'default' }}>
                  <div className="pkg-card-name"><Code2 size={12} style={{ color: typeColor }} />{pkg.name}{pkg.type && pkg.type !== 'unknown' && (<span className="badge" style={{ background: `${typeColor}15`, color: typeColor, fontSize: 9 }}>{pkg.type}</span>)}</div>
                  <div className="pkg-card-path">{pkg.path}</div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
                    {pkg.dependency_count > 0 && <span>{pkg.dependency_count} deps</span>}
                    {pkg.test_count > 0 && <span>{pkg.test_count} tests</span>}
                    {pkg.framework && <span>{pkg.framework}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Recent Journal */}
      <div className="section-label" style={{ marginBottom: 8 }}>Recent Journal</div>
      <RecentJournal projectId={projectId} />

      {/* Recent pipeline runs */}
      {(() => {
        // Auto-collapse old failed runs: keep all non-failed + only the most-recent
        // failed one (runs are newest-first). Stops the repeated-restart pileup from
        // burying the real state. "Clear All" still purges; this is display-only.
        const FAILED_STATUSES = new Set(['failed', 'error', 'cancelled']);
        let keptOneFailed = false;
        const prunedRuns = runs?.filter((r: PipelineRun) => {
          if (!FAILED_STATUSES.has(r.status)) return true;
          if (keptOneFailed) return false;
          keptOneFailed = true;
          return true; // keep the first (most recent) failed run only
        });
        const visibleRuns = prunedRuns && !showAllRuns ? prunedRuns.slice(0, 5) : prunedRuns;
        const hasFailedRuns = runs?.some((r: PipelineRun) => r.status === 'failed' || r.status === 'error');
        const handleDismissRun = async (runId: string) => { await api.dismissRun(runId).catch(() => {}); refetchRuns(); };
        const handleDismissAllRuns = async () => { if (!(await confirmDialog('Dismiss all failed pipeline runs?'))) return; await api.dismissAllRuns(projectId).catch(() => {}); refetchRuns(); };
        const getRunTitle = (r: PipelineRun) => {
          if ((r as any).task_summary) return (r as any).task_summary;
          if (r.input_text && r.input_text.length > 0 && r.trigger === 'slash_command') return r.input_text.length > 60 ? r.input_text.substring(0, 60) + '\u2026' : r.input_text;
          if (r.issue_number) return `Issue #${r.issue_number}`;
          if (r.branch || r.branch_name) return (r.branch || r.branch_name)!;
          return r.trigger;
        };
        return <>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span className="section-label" style={{ marginBottom: 0 }}>Recent Changes</span>
          {runsLoading && <div className="spinner" style={{ width: 12, height: 12 }} />}
          <div style={{ flex: 1 }} />
          {hasFailedRuns && (
            <button className="btn btn-sm" style={{ fontSize: 'var(--text-xs)', padding: '2px 8px', opacity: 0.7 }} onClick={handleDismissAllRuns}>Clear All</button>
          )}
        </div>
        {visibleRuns && visibleRuns.length > 0 ? (
          <div className="card" style={{ padding: 0 }}>
            {visibleRuns.map((r: PipelineRun, i: number) => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: i < visibleRuns.length - 1 ? "1px solid var(--border)" : "none" }}>
                <Dot status={r.status} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--text)" }}>{getRunTitle(r)}</span>
                    {r.route && <span className="badge" style={{ background: `${eC(r.route.replace("claude_", ""))}15`, color: eC(r.route.replace("claude_", "")) }}>{r.route}</span>}
                  </div>
                  <div className="mono muted" style={{ fontSize: "var(--text-xs)", marginTop: 2 }}>{r.branch || r.branch_name || r.id.slice(0, 8)} &middot; {r.status} &middot; {r.started_at ? timeAgo(r.started_at) : ""}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="dim" style={{ fontSize: "var(--text-xs)" }}>{r.duration_ms ? formatDuration(r.duration_ms) : r.status}</div>
                  {r.cost != null && <div className="mono muted" style={{ fontSize: "var(--text-xs)" }}>${r.cost.toFixed(2)}</div>}
                </div>
                {r.pr_number ? (
                  <a href={`https://github.com/${project?.repo?.owner}/${project?.repo?.name}/pull/${r.pr_number}`} target="_blank" rel="noopener noreferrer" title={`PR #${r.pr_number}`} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <CheckCircle size={11} style={{ color: "var(--green)", opacity: 0.6 }} />
                  </a>
                ) : null}
                {(r.status === 'failed' || r.status === 'error') && (
                  <button className="btn btn-sm" style={{ padding: '2px 4px', opacity: 0.5 }} title="Dismiss" onClick={() => handleDismissRun(r.id)}><X size={11} /></button>
                )}
              </div>
            ))}
            {prunedRuns && prunedRuns.length > 5 && !showAllRuns && (
              <div style={{ padding: "8px 14px", textAlign: "center", borderTop: "1px solid var(--border)" }}>
                <button className="btn btn-sm" style={{ fontSize: 'var(--text-xs)' }} onClick={() => setShowAllRuns(true)}>View all {prunedRuns.length} runs &rarr;</button>
              </div>
            )}
          </div>
        ) : !runsLoading && (
          <div className="empty-state" style={{ border: "1px dashed var(--border)", borderRadius: "var(--radius-lg)", padding: '16px' }}>
            <span className="muted" style={{ fontSize: "var(--text-sm)" }}>
              No changes yet. <span style={{ color: 'var(--blue)', cursor: 'pointer', fontWeight: 500 }} onClick={() => { void api.createSubscriberChat(projectId).then((r) => go(`subscriber-chat:chat:${r.chatSessionId}`)).catch(() => go(`subscriber-chat:project:${projectId}`)); }}>Open Chat</span> and pick what you want fixed.
            </span>
          </div>
        )}
        </>;
      })()}
      </>}

      {/* Doc Viewer Modal */}
      {viewingDoc && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }} onClick={() => setViewingDoc(null)}>
          <div style={{ background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--border)', borderRadius: 12, width: '90%', maxWidth: 720, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--text-base)', color: 'var(--text)' }}>{viewingDoc.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <VersionHistory projectId={projectId} filename={viewingDoc.name} currentContent={viewingDoc.content} onSelectVersion={(content: string, version: number) => setViewingDoc({ name: `${viewingDoc.name.replace(/ \(v\d+\)$/, '')} (v${version})`, content })} />
                <ExportMenu name={viewingDoc.name} content={viewingDoc.content} />
                <button onClick={() => setViewingDoc(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={16} /></button>
              </div>
            </div>
            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, fontSize: 'var(--text-sm)', lineHeight: 1.7, color: 'var(--text)', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap' }}>{viewingDoc.content}</div>
          </div>
        </div>
      )}
    </div>
  );
}
